import { anyRarity } from './content.js';
/**
 * The swamp's chain, first half (ADR 0047): Brumevase and its craftsmen, the
 * ferryman's channels, the Cimetière Englouti and its gravedigger, the
 * alchemist's venom, and the caiman huntress by the south road. The second
 * half (`swamp-depths.ts`) goes on to the Ruines Noyées and the troll.
 */
export const ELDER = { mob: 'swamp-elder', biome: 'swamp' };
export const FERRYMAN = { mob: 'swamp-ferryman', biome: 'swamp' };
export const GRAVEDIGGER = { mob: 'swamp-gravedigger', biome: 'swamp' };
export const SCHOLAR = { mob: 'swamp-scholar', biome: 'swamp' };
export const HUNTER = { mob: 'swamp-hunter', biome: 'swamp' };
export const SMITH = { mob: 'smith', biome: 'swamp' };
export const SMELTER = { mob: 'smelter', biome: 'swamp' };
export const ALCHEMIST = { mob: 'alchemist', biome: 'swamp' };
export const MERCHANT = { mob: 'merchant', biome: 'swamp' };
export const SWAMP_VILLAGE_QUESTS = [
    {
        id: 'swamp-welcome',
        name: 'Les brumes de Brumevase',
        biome: 'swamp',
        level: 8,
        giver: ELDER,
        turnIn: ELDER,
        offer: 'Alors tu restes avec nous ? Très bien. Ici, celui qui reste seul finit dans la vase. Touche l’arche de la place, puis va dire bonjour au forgeron, à l’alchimiste et au marchand. Comme ça, ils sauront qui repêcher si tu tombes dans une mare.',
        summary: 'Enregistrer le portail de Brumevase, saluer le forgeron, l’alchimiste et le marchand, puis revenir à la Doyenne.',
        pending: 'L’arche est au milieu de la place. Les autres sont tout autour, sous les lanternes.',
        complete: 'Tu sais que je n’ai pas vu le soleil depuis mes douze ans ? Avant, la brume se levait à midi. Maintenant, elle ne se lève plus jamais. Assieds-toi, je vais t’expliquer ce qui se passe ici.',
        objectives: [
            { kind: 'portal', label: 'Enregistrer le portail de Brumevase', portal: 'swamp' },
            {
                kind: 'talk',
                label: 'Saluer le forgeron',
                npc: SMITH,
                line: 'Fais voir ton épée. Rouillée, évidemment ! Ici, tout rouille : mes outils, ma porte, et même mon genou. Rapporte-moi de l’argile des marais, je te traiterai ça pour que ça tienne.',
            },
            {
                kind: 'talk',
                label: 'Saluer l’alchimiste',
                npc: ALCHEMIST,
                line: 'De la bave de crapaud, ça sent aussi mauvais que ça en a l’air, mais ça soigne mieux que n’importe quoi ! Rapporte-moi ce que tu trouves sur les bêtes du marais, je t’en ferai des potions.',
            },
            {
                kind: 'talk',
                label: 'Saluer le marchand',
                npc: MERCHANT,
                line: 'Un client ! Un vrai ? Assieds-toi ! Ah non, pas sur ce banc, il est pourri. J’ai des armures en écailles, parfaites pour le marais. Reviens avec de l’or, je te ferai un prix. Je fais des prix à tout le monde, de toute façon, il n’y a personne.',
            },
        ],
        reward: { xp: 200, gold: 40 },
    },
    {
        id: 'swamp-toads',
        name: 'Le chant des crapauds',
        biome: 'swamp',
        level: 8,
        requires: ['swamp-welcome'],
        giver: ELDER,
        turnIn: ELDER,
        offer: 'Les crapauds géants chantent sous ma fenêtre toutes les nuits ! Avant, la brume les endormait. Maintenant, elle les excite. J’ai quatre-vingts ans et je n’arrive plus à dormir. Fais-les taire un peu, s’il te plaît. Pas tous, j’aime bien les entendre de loin.',
        summary: 'Faire taire les crapauds géants qui s’approchent de Brumevase.',
        pending: 'Les crapauds sont au bord des mares, tout autour du village. Frappe avant qu’ils sautent !',
        complete: 'Écoute… Plus rien ! Je vais enfin dormir cette nuit. L’alchimiste t’a laissé des potions. Ne lui demande pas avec quoi elles sont faites.',
        objectives: [{ kind: 'kill', label: 'Crapauds géants abattus', mobs: ['toad'], count: 16 }],
        reward: { xp: 380, gold: 75, items: [{ templateId: 'swamp-tonic', quantity: 3 }] },
    },
    {
        id: 'swamp-scales',
        name: 'Peau d’écailles',
        biome: 'swamp',
        level: 8,
        requires: ['swamp-welcome'],
        giver: MERCHANT,
        turnIn: MERCHANT,
        offer: 'Tes vêtements de la forêt vont pourrir en trois jours, ici ! L’écaille, elle, ne prend pas l’eau. Achète un Casque et une Cuirasse d’écailles et mets-les. Je te les fais presque à prix coûtant. Et puis comme ça, quelqu’un portera enfin ma marchandise !',
        summary: 'Acheter et porter le Casque et la Cuirasse d’écailles du marchand de Brumevase.',
        pending: 'Achète le casque et la cuirasse et mets-les sur toi. Pas dans ton sac, il pourrit aussi !',
        complete: 'Tu vois ? Tu es au sec ! Enfin, un peu moins mouillé. Les hommes-lézards portent des écailles encore plus solides. Si tu en trouves, garde-les, je n’ai pas les moyens de te les racheter.',
        objectives: [
            { kind: 'wear', label: 'Porter un Casque d’écailles', items: anyRarity('scale-helm') },
            { kind: 'wear', label: 'Porter une Cuirasse d’écailles', items: anyRarity('scale-chest') },
        ],
        reward: { xp: 150, gold: 30 },
    },
    {
        id: 'swamp-ferry',
        name: 'La barque du passeur',
        biome: 'swamp',
        level: 8,
        requires: ['swamp-toads'],
        giver: ELDER,
        turnIn: FERRYMAN,
        offer: 'Ansgar, le passeur, est au nord du village. Sa barque prend l’eau, et sans elle, on ne peut plus aller au cimetière. Il bouche les trous avec de la bave de crapaud. Apporte-lui-en ! Il ne te dira pas merci, mais ce n’est pas contre toi, il ne dit merci à personne.',
        summary: 'Rapporter de la bave de crapaud au Passeur Ansgar, au nord de Brumevase.',
        pending: 'Ansgar, c’est le monsieur au grand chapeau de paille, au nord. N’oublie pas la bave de crapaud !',
        complete: 'Pose ça là. Bon, ma barque va tenir. Elle tient toujours, elle est plus solide que moi. Prends cette argile, j’en ai plus que de barques, et le forgeron en réclame tout le temps.',
        objectives: [
            { kind: 'collect', label: 'Bave de crapaud pour la barque', item: 'toad-slime', count: 6 },
        ],
        reward: { xp: 250, gold: 50, items: [{ templateId: 'bog-clay', quantity: 3 }] },
    },
    {
        id: 'swamp-forge',
        name: 'Écailles trempées',
        biome: 'swamp',
        level: 9,
        requires: ['swamp-scales', 'swamp-ferry'],
        giver: SMITH,
        turnIn: SMITH,
        offer: 'Ansgar m’a dit que tu avais de l’argile ! Achète d’abord une Lame d’écailles chez le marchand, il en a trois qui prennent la poussière. Ensuite, apporte-moi une pièce et on la monte à trois crans. Et va voir le fondeur, il te montrera ce qu’on fait avec les vieilles pièces.',
        summary: 'Porter une Lame d’écailles, faire monter une pièce portée à +3 et saluer le fondeur de Brumevase.',
        pending: 'Il me faut une pièce à trois crans. De l’argile, de l’or, et vite, avant que tout rouille !',
        complete: 'Voilà ! Graisse-la tous les soirs, sinon elle rouille. Tiens, encore de l’argile. Garde-la au sec. Enfin, si tu trouves un endroit sec par ici.',
        objectives: [
            { kind: 'wear', label: 'Porter une Lame d’écailles', items: anyRarity('scale-sword') },
            { kind: 'upgrade', label: 'Une pièce portée à +3', level: 3 },
            {
                kind: 'talk',
                label: 'Saluer le fondeur',
                npc: SMELTER,
                line: 'Une pièce qui ne te sert plus, je la fonds, et ça donne de quoi en renforcer une autre. L’an dernier, j’ai fondu la grille du cimetière. Depuis, Guénola ne me parle plus.',
            },
        ],
        reward: { xp: 250, gold: 50, items: [{ templateId: 'bog-clay', quantity: 2 }] },
    },
    {
        id: 'swamp-reeds',
        name: 'Les rôdeurs des roseaux',
        biome: 'swamp',
        level: 9,
        requires: ['swamp-ferry'],
        giver: FERRYMAN,
        turnIn: FERRYMAN,
        offer: 'Ma barque flotte, mais à quoi bon ? Les hommes-lézards se cachent dans les roseaux et coulent tout ce qui passe. Deux barques ce mois-ci ! Ils n’osaient pas avant. Chasse-les des berges, s’il te plaît.',
        summary: 'Chasser les hommes-lézards qui rôdent dans les roseaux du marais.',
        pending: 'Les hommes-lézards vont toujours par deux. Quand tu en vois un, l’autre est derrière toi.',
        complete: 'C’est calme, merci. Tiens, des potions. Ma femme en faisait, il m’en reste une caisse. Elle est au cimetière, d’ailleurs. Ma femme, hein, pas la caisse.',
        objectives: [{ kind: 'kill', label: 'Hommes-lézards chassés', mobs: ['lizardman'], count: 15 }],
        reward: { xp: 450, gold: 90, items: [{ templateId: 'swamp-tonic', quantity: 2 }] },
    },
    {
        id: 'swamp-cemetery',
        name: 'Le Cimetière Englouti',
        biome: 'swamp',
        level: 9,
        requires: ['swamp-reeds'],
        giver: FERRYMAN,
        turnIn: GRAVEDIGGER,
        offer: 'Guénola, la fossoyeuse, s’occupe du cimetière, au nord, toute seule. D’habitude, elle vient chercher son pain toutes les semaines. Là, ça fait un mois qu’on ne l’a pas vue. Va voir si tout va bien. Moi, je n’y vais plus, les tombes bougent.',
        summary: 'Rejoindre le Cimetière Englouti, au nord, et parler à la Fossoyeuse Guénola.',
        pending: 'Le cimetière est au nord, là où les croix sortent de la vase.',
        complete: 'C’est Ansgar qui t’envoie ? Dis-lui que je mange bien, il s’inquiète pour un rien. Mais viens voir : douze tombes ont été ouvertes cette semaine. Et quelqu’un a creusé pour entrer, pas pour sortir. Ouf.',
        objectives: [
            { kind: 'reach', label: 'Atteindre le Cimetière Englouti', landmark: 'Cimetière Englouti' },
        ],
        reward: { xp: 200, gold: 40 },
    },
    {
        id: 'swamp-graves',
        name: 'Les pilleurs de tombes',
        biome: 'swamp',
        level: 9,
        requires: ['swamp-cemetery'],
        giver: GRAVEDIGGER,
        turnIn: GRAVEDIGGER,
        offer: 'Ce sont les hommes-lézards ! Ils viennent la nuit, ils creusent, et ils emportent les os. Juste les os, même pas les bijoux, c’est bizarre. Chasse-les et rapporte-moi leurs écailles, la Doyenne voudra une preuve.',
        summary: 'Chasser les hommes-lézards du Cimetière Englouti et rapporter leurs écailles à la fossoyeuse.',
        pending: 'Les hommes-lézards rôdent dans le cimetière. Je les entends gratter le soir.',
        complete: 'Merci ! Je vais pouvoir reboucher tout ça. Prends ces gantelets, c’étaient ceux du dernier gardien du cimetière. Il est là, troisième rangée. Il ne dira rien, va.',
        objectives: [
            { kind: 'kill', label: 'Pilleurs lézards chassés', mobs: ['lizardman'], count: 16 },
            { kind: 'collect', label: 'Écailles de lézard', item: 'lizard-scale', count: 6 },
        ],
        reward: {
            xp: 600,
            gold: 120,
            items: [{ templateId: 'scale-gloves-rare', quantity: 1 }],
        },
    },
    {
        id: 'swamp-lanterns',
        name: 'Les lanternes des noyés',
        biome: 'swamp',
        level: 10,
        requires: ['swamp-graves'],
        giver: GRAVEDIGGER,
        turnIn: ELDER,
        offer: 'Il y a autre chose. La nuit, des lumières vertes flottent au-dessus des tombes, et la brume pique les yeux. Va demander à l’alchimiste ce que c’est, puis préviens la Doyenne.',
        summary: 'Interroger l’alchimiste de Brumevase sur la brume empoisonnée, puis prévenir la Doyenne.',
        pending: 'Va voir l’alchimiste, puis la Doyenne. Et rince-toi les yeux !',
        complete: 'Du poison dans la brume, des os volés… Mon grand-père parlait d’une créature cachée dans le Bourbier, au nord, quand il avait un peu trop bu. Tout le monde se moquait de lui. On aurait peut-être dû l’écouter.',
        objectives: [
            {
                kind: 'talk',
                label: 'Interroger l’alchimiste',
                npc: ALCHEMIST,
                line: 'Des lumières vertes ? Et ça pique les yeux ? C’est du venin de crapaud, porté par la brume. Mais il y en a beaucoup trop pour un crapaud normal ! Il doit y en avoir un énorme, quelque part. Va le dire à la Doyenne.',
            },
        ],
        reward: { xp: 150, gold: 30 },
    },
    {
        id: 'swamp-venom',
        name: 'Le venin qui dort',
        biome: 'swamp',
        level: 10,
        requires: ['swamp-lanterns'],
        giver: ALCHEMIST,
        turnIn: ALCHEMIST,
        offer: 'Ce crapaud géant ne sort jamais de jour. Mais il adore les vers de tourbe, et j’en élève dans un seau ! Prends celui-ci et pose-le près d’un repaire de crapauds, loin du village. Et évite son crachat, ça brûle.',
        summary: 'Poser le ver de tourbe près d’un repaire de crapauds géants, puis abattre le Crapaud venimeux qui en sort.',
        pending: 'Pose le ver près d’un repaire de crapauds, loin du village. S’il s’échappe, j’en ai d’autres dans le seau.',
        complete: 'Bravo ! Avec son venin, je vais faire un remède pour tout le village. Tiens, deux potions. Celles-là, tu peux me demander avec quoi elles sont faites.',
        grants: [{ templateId: 'peat-worm', quantity: 1 }],
        objectives: [
            { kind: 'kill', label: 'Crapaud venimeux abattu', mobs: ['venom-toad'], count: 1 },
        ],
        reward: { xp: 450, gold: 90, items: [{ templateId: 'swamp-potion', quantity: 3 }] },
    },
    {
        id: 'swamp-hunter',
        name: 'La traqueuse du sud',
        biome: 'swamp',
        level: 10,
        requires: ['swamp-lanterns'],
        giver: ELDER,
        turnIn: HUNTER,
        offer: 'Pour aller au Bourbier, il faut traverser les canaux, et ils sont pleins de caïmans. Nella, la traqueuse, les chasse depuis qu’elle sait marcher. Elle t’attend à la sortie sud. Mais elle ne parle qu’aux gens qui ont déjà chassé des caïmans, alors bats-en quelques-uns avant.',
        summary: 'Abattre quelques caïmans, puis rejoindre la Traqueuse Nella au sud de Brumevase.',
        pending: 'Les caïmans dorment au bord de l’eau. Ils se réveillent quand tu passes à côté.',
        complete: 'Tu sens le caïman, bien ! Les derniers que la Doyenne m’a envoyés sentaient le savon. Ils ne sont pas restés longtemps.',
        objectives: [{ kind: 'kill', label: 'Caïmans abattus', mobs: ['caiman'], count: 8 }],
        reward: { xp: 350, gold: 70 },
    },
    {
        id: 'swamp-teeth',
        name: 'Les mâchoires du chenal',
        biome: 'swamp',
        level: 10,
        requires: ['swamp-hunter'],
        giver: HUNTER,
        turnIn: HUNTER,
        offer: 'Il y a deux fois plus de caïmans qu’avant. Quelqu’un les nourrit, j’en suis sûre. Et en plus, quelque chose les mange, des gros, en entier ! Aide-moi à nettoyer les canaux et rapporte-moi leurs dents. Ici, un collier de dents de caïman, ça impressionne tout le monde.',
        summary: 'Nettoyer les chenaux de leurs caïmans et rapporter leurs dents à la traqueuse.',
        pending: 'Un caïman met du temps à ouvrir la gueule. Frappe, puis recule avant qu’il la referme !',
        complete: 'Ça, c’est un beau collier ! Et prends ces bottes, je les ai trouvées dans le ventre d’un caïman. Elles sont comme neuves. Leur ancien propriétaire, un peu moins.',
        objectives: [
            { kind: 'kill', label: 'Caïmans des chenaux', mobs: ['caiman'], count: 16 },
            { kind: 'collect', label: 'Dents de caïman', item: 'caiman-tooth', count: 6 },
        ],
        reward: { xp: 650, gold: 130, items: [{ templateId: 'scale-boots-rare', quantity: 1 }] },
    },
    {
        id: 'swamp-camp',
        name: 'Le camp des écailles',
        biome: 'swamp',
        level: 11,
        requires: ['swamp-teeth'],
        giver: HUNTER,
        turnIn: HUNTER,
        offer: 'J’ai trouvé où finissent les os volés ! Les bêtes du marais gardent des camps, avec un coffre au milieu. Bats les gardiens d’un camp et ouvre le coffre. Je veux savoir ce qu’un lézard peut bien garder dans un coffre.',
        summary: 'Vaincre les gardiens d’un camp du marais et ouvrir son coffre.',
        pending: 'Le coffre ne s’ouvre que quand tous les gardiens sont battus. Tous, j’ai dit !',
        complete: 'Des os couverts de dessins en spirale… On ne garde pas ce genre de chose pour le revendre. Ça ne me plaît pas du tout. Tiens, des potions, et ce qui me reste d’argile.',
        objectives: [{ kind: 'camp', label: 'Coffre d’un camp du marais', biome: 'swamp', count: 1 }],
        reward: {
            xp: 500,
            gold: 100,
            items: [
                { templateId: 'swamp-potion', quantity: 2 },
                { templateId: 'bog-clay', quantity: 2 },
            ],
        },
    },
];
//# sourceMappingURL=swamp-village.js.map